export interface Coupon {
  code: string;
  fullPrice: number;
  discountedPrice: number;
  discountDescription: string;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  durationDays: number;
  durationMonths: number;
  priceCents: number;
  currency: string;
  initialAmount?: number;
  installmentInterval?: 'week' | 'month';
  installmentIntervalCount?: number;
  planSlug: string;
  slug: string;
  preselected: boolean;
  hasLiveClasses: boolean;
  cohortOptions: Cohort[];
}

export interface LiveClassSession {
  startTime: number;
  endTime: number;
}

export interface BulkSelfServeFormProps {
  authenticityToken: string;
  subscriptionPlans: SubscriptionPlan[];
  nonprofit: boolean;
}

export interface SubscriptionFormProps {
  allowInstallments: boolean;
  bulkOrder: boolean;
  stripeToken?: string;
  authenticityToken: string;
  // subscriptionPlans are provided for bulk orders, and are the actual plans being bought.
  subscriptionPlans: SubscriptionPlan[];
  // subscriptionPlanOptions are for single orders, and one will be chosen for the actual purchase.
  subscriptionPlanOptions: SubscriptionPlan[];
  discount?: Discount;
  signedIn: boolean;
  isEuUser: boolean;
  beforeSubmit?: () => Promise<boolean>;
  formikRef?: any;
  twoStepSignup: boolean;
  user?: {
    email: string;
    password: string;
    passwordConfirmation: string;
    termsAndPolicyAccepted: boolean;
  };

  cohortId?: number;
}

export interface Cohort {
  id: number;
  name: string;
  liveClassSessions: LiveClassSession[];
  onDemand: boolean;
}

export interface OrderItem {
  purchasable: SubscriptionPlan;
  purchasableCount: number;
  priceCentsPerItem: number;
}

export interface BulkSelfServeFormValues {
  authenticityToken: string;
  orderItems: OrderItem[];
}

export interface SubscriptionFormValues {
  authenticityToken: string;
  cardCvc?: string;
  cardExpirationDateMonth?: number;
  cardExpirationDateYear?: number;
  cardName?: string;
  cardNumber?: string;
  cardZipCode?: string;
  cohortId?: number;
  passwordConfirmation?: string;
  discount?: Discount;
  couponCode?: string;
  email?: string;
  paymentStyle?: PaymentStyle;
  orderItems: OrderItem[];
  password?: string;
  paymentMethod: PaymentMethod;
  purchaseToken?: string;
  selectedPlanId?: number;
  signedIn: boolean;
  stripePaymentMethod?: any;
  termsAndPolicyAccepted: boolean;
  alipayPaymentUrl?: string; // Used for redirecting to Alipay payment page
  _error?: string;
}

export interface InstallmentsFormGroupProps {
  values: SubscriptionFormValues;
  setFieldValue: (field: string, value: any) => void;
  formattedDiscountPrice: string;
  formattedInstallmentPrice: string;
  formattedInstallmentsDates: string[];
}

export interface BulkPaymentFormGroupProps {
  errors?: any;
}

export interface StripePaymentFormGroupProps {
  values: SubscriptionFormValues;
  setFieldValue: (field: string, value: any) => void;
  formattedDiscountPrice: string;
  errors?: any;
  paymentRequestMethod: string;
}

export interface SingleConfirmationFormGroupProps {
  values: SubscriptionFormValues;
  formattedexpirationDate: string;
  formattedPrice: string;
  formattedDiscountPrice: string;
  formattedInstallmentPrice: string;
  showPlanName: boolean;
  setFieldValue: (field: string, value: any) => void;
}

export interface MultiConfirmationFormGroupProps {
  values: BulkSelfServeFormValues;
  errors?: any;
  requirePersonalizedPackage: boolean;
  variant?: string;
  className?: string;
}

export enum PaymentStyle {
  FULLPAYMENT = 'FULLPAYMENT',
  INSTALLMENTS = 'INSTALLMENTS'
}

export enum PaymentMethod {
  CREDITCARD = 'credit_card',
  PAYPAL = 'paypal_express',
  ALIPAY = 'stripe_source',
  STRIPE = 'stripe',
  STRIPE_PAYMENT_REQUEST = 'stripe_payment_request'
}

export interface Discount {
  upgrade: boolean;
  couponCode: string;
  fullPrice: number;
  compoundPrice?: CompoundPrice[];
  discountedPrice: number;
  discountDescription: string;
}

export interface CompoundPrice {
  name: string;
  fullPrice?: number;
  price: number;
}
